import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';

const compressImage = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const size = 200;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      const min = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.75));
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});

const getPasswordStrength = (pwd) => {
  if (!pwd) return null;
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { level: 'Faible', color: 'bg-red-500', width: 'w-1/4', text: 'text-red-400' };
  if (score <= 3) return { level: 'Moyen', color: 'bg-yellow-500', width: 'w-2/4', text: 'text-yellow-400' };
  return { level: 'Fort', color: 'bg-green-500', width: 'w-full', text: 'text-green-400' };
};

const DriverCard = ({ driver, selected, onSelect }) => {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? null : driver)}
      className={`relative flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${
        selected
          ? 'border-current shadow-lg scale-[1.03]'
          : 'border-zinc-800 hover:border-zinc-600'
      }`}
      style={selected ? { borderColor: driver.color, boxShadow: `0 0 12px ${driver.color}44` } : {}}
    >
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: driver.color }}>
          <Icon name="check" size={9} className="text-white" />
        </div>
      )}
      <div className="w-14 h-14 rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center border" style={{ borderColor: `${driver.color}44` }}>
        {!imgFailed ? (
          <img src={driverImgUrl(driver.img)} alt={driver.name} className="w-full h-full object-cover object-top" onError={() => setImgFailed(true)} />
        ) : (
          <span className="text-xs font-black" style={{ color: driver.color }}>{driver.name.split(' ').map(n => n[0]).join('')}</span>
        )}
      </div>
      <div className="text-center">
        <p className="text-white text-[10px] font-bold leading-tight">{driver.name.split(' ').at(-1)}</p>
        <p className="text-[9px] font-medium mt-0.5" style={{ color: driver.color }}>{driver.number}</p>
      </div>
    </button>
  );
};

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '' });
  const [avatar, setAvatar] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [favoriteDriver, setFavoriteDriver] = useState(null);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatar(await compressImage(file));
  };

  const strength = getPasswordStrength(form.password);

  const validateStep1 = () => {
    const e = {};
    if (!form.username || form.username.length < 3) e.username = 'Minimum 3 caractères';
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (!form.password || form.password.length < 6) e.password = 'Minimum 6 caractères';
    if (form.password !== form.confirm) e.confirm = 'Les mots de passe ne correspondent pas';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form.username, form.email, form.password, avatar, favoriteDriver);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'inscription");
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-black text-white">Inscription</h1>
          <p className="text-zinc-400 mt-1">Rejoins la ligue des pronostiqueurs F1</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6 px-1">
          {[1, 2].map(s => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                  step >= s ? 'bg-f1red text-white' : 'bg-zinc-800 text-zinc-500'
                }`}>{step > s ? <Icon name="check" size={12} /> : s}</div>
                <span className={`text-xs font-medium ${step === s ? 'text-white' : 'text-zinc-500'}`}>
                  {s === 1 ? 'Mon compte' : 'Mon pilote'}
                </span>
              </div>
              {s < 2 && <div className={`flex-1 h-px transition-all ${step > s ? 'bg-f1red' : 'bg-zinc-800'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <form onSubmit={handleNext} className="card flex flex-col gap-4">
            {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}

            {/* Avatar */}
            <div className="flex flex-col items-center gap-2 pb-1">
              <button type="button" onClick={() => fileRef.current?.click()}
                className="relative group w-24 h-24 rounded-full overflow-hidden border-2 border-dashed border-zinc-700 hover:border-f1red transition-colors bg-zinc-800 flex items-center justify-center">
                {avatar ? (
                  <>
                    <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Icon name="upload" size={20} className="text-white" />
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-zinc-500 group-hover:text-f1red transition-colors">
                    <Icon name="photo" size={24} />
                    <span className="text-[10px] uppercase tracking-wider font-semibold">Photo</span>
                  </div>
                )}
              </button>
              <p className="text-zinc-600 text-xs">Photo de profil (optionnel)</p>
              {avatar && (
                <button type="button" onClick={() => setAvatar(null)} className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1 transition-colors">
                  <Icon name="x" size={12} /> Supprimer
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
            </div>

            {/* Username */}
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider block mb-1">Pseudo</label>
              <input type="text" className={`input ${errors.username ? 'border-red-500' : ''}`} placeholder="Max33"
                value={form.username} onChange={e => { setForm(f => ({ ...f, username: e.target.value })); setErrors(er => ({ ...er, username: '' })); }}
                minLength={3} />
              {errors.username && <p className="text-red-400 text-xs mt-1">{errors.username}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider block mb-1">Email</label>
              <input type="email" className={`input ${errors.email ? 'border-red-500' : ''}`} placeholder="ton@email.com"
                value={form.email} onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setErrors(er => ({ ...er, email: '' })); }} />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider block mb-1">Mot de passe</label>
              <div className="relative">
                <input type={showPwd ? 'text' : 'password'} className={`input pr-10 ${errors.password ? 'border-red-500' : ''}`} placeholder="••••••••"
                  value={form.password} onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setErrors(er => ({ ...er, password: '' })); }}
                  minLength={6} />
                <button type="button" onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                  <Icon name={showPwd ? 'eyeOff' : 'eye'} size={16} />
                </button>
              </div>
              {form.password && strength && (
                <div className="mt-2">
                  <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${strength.color} ${strength.width}`} />
                  </div>
                  <p className={`text-xs mt-1 ${strength.text}`}>{strength.level}</p>
                </div>
              )}
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Confirm password */}
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider block mb-1">Confirmer le mot de passe</label>
              <div className="relative">
                <input type={showConfirm ? 'text' : 'password'} className={`input pr-10 ${errors.confirm ? 'border-red-500' : ''}`} placeholder="••••••••"
                  value={form.confirm} onChange={e => { setForm(f => ({ ...f, confirm: e.target.value })); setErrors(er => ({ ...er, confirm: '' })); }} />
                <button type="button" onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                  <Icon name={showConfirm ? 'eyeOff' : 'eye'} size={16} />
                </button>
              </div>
              {form.confirm && form.password && (
                <p className={`text-xs mt-1 flex items-center gap-1 ${form.password === form.confirm ? 'text-green-400' : 'text-red-400'}`}>
                  <Icon name={form.password === form.confirm ? 'check' : 'x'} size={11} />
                  {form.password === form.confirm ? 'Mots de passe identiques' : 'Ne correspondent pas'}
                </p>
              )}
              {errors.confirm && <p className="text-red-400 text-xs mt-1">{errors.confirm}</p>}
            </div>

            <button type="submit" className="btn-primary w-full mt-1">
              Suivant →
            </button>
          </form>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="card flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-white text-lg">Ton pilote favori</h2>
              <p className="text-zinc-500 text-sm mt-0.5">Choisis le pilote que tu supportes cette saison</p>
            </div>

            <div className="grid grid-cols-4 gap-2 max-h-80 overflow-y-auto pr-1">
              {F1_DRIVERS.map(driver => (
                <DriverCard key={driver.number} driver={driver}
                  selected={favoriteDriver?.number === driver.number}
                  onSelect={setFavoriteDriver} />
              ))}
            </div>

            {favoriteDriver && (
              <div className="flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ borderColor: `${favoriteDriver.color}44`, backgroundColor: `${favoriteDriver.color}11` }}>
                <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800 border-2" style={{ borderColor: favoriteDriver.color }}>
                  <img src={driverImgUrl(favoriteDriver.img)} alt={favoriteDriver.name} className="w-full h-full object-cover object-top"
                    onError={e => { e.target.style.display = 'none'; }} />
                </div>
                <div>
                  <p className="text-white text-sm font-bold">{favoriteDriver.name}</p>
                  <p className="text-xs font-medium" style={{ color: favoriteDriver.color }}>{favoriteDriver.team}</p>
                </div>
                <button type="button" onClick={() => setFavoriteDriver(null)} className="ml-auto text-zinc-500 hover:text-zinc-300 transition-colors">
                  <Icon name="x" size={14} />
                </button>
              </div>
            )}

            {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1">
                ← Retour
              </button>
              <button type="submit" className="btn-primary flex-1" disabled={loading}>
                {loading ? 'Création...' : 'Créer mon compte'}
              </button>
            </div>
            {!favoriteDriver && (
              <button type="submit" className="text-xs text-zinc-500 hover:text-zinc-300 text-center transition-colors -mt-2" disabled={loading}>
                Passer cette étape →
              </button>
            )}
          </form>
        )}

        <p className="text-center text-zinc-500 text-sm mt-4">
          Déjà un compte ?{' '}
          <Link to="/login" className="text-f1red hover:underline">Se connecter</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
